import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, X, ShieldCheck, FileText } from '@phosphor-icons/react';
import { backdropClick, stopBackdropPropagation } from '../lib/modalBackdrop';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal per caricamento del documento d'identità (KYC) di un lead.
 * Quando l'upload va a buon fine richiama `onSuccess()` (es. ritry invio).
 *
 * Props:
 *   leadId, leadName, open, onClose, onSuccess
 *   allowAdminSkip (bool) + onSkip: mostra il bypass "Invia senza documento" (solo admin)
 */
export default function KycUploadModal({ leadId, leadName, open, onClose, onSuccess, allowAdminSkip, onSkip }) {
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState('CI');
  const [docNumber, setDocNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState(null);

  const reload = useCallback(async () => {
    if (!leadId) return;
    try {
      const r = await fetch(`${API}/api/leads/${leadId}/identity-doc`, { credentials: 'include' });
      if (r.ok) setMeta(await r.json());
    } catch (e) { /* noop */ }
  }, [leadId]);

  useEffect(() => {
    if (open) {
      setFile(null); setDocNumber(''); setExpiry(''); setDocType('CI');
      reload();
    }
  }, [open, reload]);

  if (!open) return null;

  const submit = async () => {
    if (!file) { toast.error('Seleziona un file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      fd.append('doc_number', docNumber);
      fd.append('expiry_date', expiry);
      const r = await fetch(`${API}/api/leads/${leadId}/identity-doc`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success('Documento d\'identità caricato. KYC OK.');
        onSuccess && onSuccess();
        onClose && onClose();
      } else {
        toast.error(d.detail || 'Errore caricamento documento');
      }
    } catch (e) {
      toast.error(`Errore di rete: ${e.message || e}`);
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async () => {
    if (!window.confirm('Rimuovere il documento attuale?')) return;
    try {
      const r = await fetch(`${API}/api/leads/${leadId}/identity-doc`, {
        method: 'DELETE', credentials: 'include',
      });
      if (r.ok) { toast.success('Documento rimosso'); setMeta(null); reload(); }
    } catch (e) { /* noop */ }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      {...backdropClick(onClose, uploading)}
      data-testid="kyc-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg"
        {...stopBackdropPropagation}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck size={20} className="text-orange-500" />
            KYC — Documento d'identità
          </h3>
          <button onClick={onClose} disabled={uploading}
                  className="p-1 hover:bg-slate-100 rounded"
                  data-testid="kyc-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-slate-600">
            Cliente: <span className="font-medium text-slate-900">{leadName || leadId}</span>
          </div>

          {meta?.has_file && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              <div className="font-medium text-emerald-800 flex items-center gap-2">
                <FileText size={16} /> Documento già caricato
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {meta.doc_type} {meta.doc_number ? `· n° ${meta.doc_number}` : ''}
                {meta.expiry_date ? ` · scad. ${meta.expiry_date}` : ''}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                {meta.file_name} · {Math.round((meta.size_bytes || 0) / 1024)} KB · caricato il {(meta.uploaded_at || '').slice(0, 16).replace('T', ' ')}
              </div>
              <button onClick={removeDoc}
                      className="text-xs text-red-600 hover:underline mt-2"
                      data-testid="kyc-remove-btn">
                Rimuovi documento
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Tipo documento</label>
              <select value={docType} onChange={e => setDocType(e.target.value)}
                      className="w-full border rounded px-2 py-2 text-sm"
                      data-testid="kyc-doctype-select">
                <option value="CI">Carta d'Identità</option>
                <option value="PATENTE">Patente</option>
                <option value="PASSAPORTO">Passaporto</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Numero documento</label>
              <Input value={docNumber} onChange={e => setDocNumber(e.target.value)}
                     placeholder="es. AX1234567" data-testid="kyc-docnumber-input" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Scadenza (opzionale)</label>
            <Input type="date" value={expiry} onChange={e => setExpiry(e.target.value)}
                   data-testid="kyc-expiry-input" />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">File (PDF / JPG / PNG · max 10 MB)</label>
            <input type="file"
                   accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   className="block w-full text-sm border rounded px-2 py-2"
                   data-testid="kyc-file-input" />
            {file && (
              <div className="text-xs text-slate-500 mt-1">
                {file.name} · {Math.round(file.size / 1024)} KB
              </div>
            )}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            ⚠️ Il documento d'identità è obbligatorio per l'invio del contratto al cliente
            (gate KYC vincolante). Verrà conservato in modo riservato.
            {allowAdminSkip && (
              <span className="block mt-1 text-slate-600">
                Come <strong>admin</strong> puoi comunque procedere senza documento: l'invio verrà tracciato come "KYC saltato".
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 border-t bg-slate-50 rounded-b-lg">
          {allowAdminSkip && onSkip && (
            <button
              onClick={() => {
                if (window.confirm('Inviare il contratto SENZA documento d\'identità?\nL\'operazione verrà tracciata come bypass KYC (solo admin).')) onSkip();
              }}
              disabled={uploading}
              className="text-xs text-red-600 hover:text-red-800 underline mr-auto"
              data-testid="kyc-admin-skip-btn">
              Invia senza documento (solo admin)
            </button>
          )}
          <div className={allowAdminSkip ? '' : 'ml-auto'} />
          <Button variant="outline" onClick={onClose} disabled={uploading}
                  data-testid="kyc-cancel-btn">
            Annulla
          </Button>
          <Button onClick={submit} disabled={uploading || !file}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="kyc-upload-btn">
            <Upload size={16} className="mr-1" />
            {uploading ? 'Caricamento…' : 'Carica documento'}
          </Button>
        </div>
      </div>
    </div>
  );
}
